/**
 * Risk-meter mappings — single source of truth for translating an
 * assessment's RiskLevel / AIRiskLevel enum value into the presentation
 * quadruple `<app-risk-meter>` needs (`scale`, `level`, `tone`, `label`).
 *
 * Lives here (not inside either consuming component) because two
 * surfaces consume the same mapping:
 *   - `<app-risk-score-display>` — assessment cards + list view
 *   - `<app-model-card-summary>` — condensed model card pillars
 *
 * Keeping the maps colocated guarantees both surfaces agree on the
 * scale → bar-count and palette, so a fix in one ripples everywhere
 * without code duplication.
 *
 * Scale is bound to the axis, not to the reading. Each axis has its
 * own tier count and the meter honestly renders that many bars — a
 * MAX reading on a 3-scale axis fills all 3 bars (looks maxed out),
 * a MAX on a 4-scale axis fills all 4. No more "always 4 bars with
 * one left outlined" for 3-tier axes.
 *
 *   GDPR / Cybersecurity  → scale 3 (LOW / MEDIUM / HIGH + UNKNOWN)
 *     LOW=1·success · MEDIUM=2·warning · HIGH=3·error · UNKNOWN=0·muted
 *   AI Act                → scale 4 (LOW / LIMITED / HIGH / PROHIBITED + UNKNOWN)
 *     LOW=1·success · LIMITED=2·warning · HIGH=3·error ·
 *     PROHIBITED=4·error-strong · UNKNOWN=0·muted
 *
 * Adding a new axis with its own tier count: add an enum in
 * assessment.constants, add a `Record<NewEnum, RiskMeterDescriptor>`
 * table below with the right scale on every row, expose a resolver
 * like `newAxisToMeter`. Consumers spread the descriptor into
 * `<app-risk-meter>` and the meter renders the right number of bars
 * automatically. No template / CSS / primitive changes required.
 */

import { AIRiskLevel, DpiaOverallRisk, RiskLevel } from '../constants/assessment.constants';
import { RiskMeterLevel, RiskMeterScale, RiskMeterTone } from '../components/risk-meter/risk-meter';
export interface RiskMeterDescriptor {
  /** Total bar count for this axis — 3 for GDPR/Cyber generic scale,
   *  4 for EU AI Act, 4 for the vendor RiskTier. The primitive renders
   *  exactly this many bars so a MAX reading looks maxed out per axis. */
  scale: RiskMeterScale;
  level: RiskMeterLevel;
  tone: RiskMeterTone;
  /** Sentence-cased severity word for the chip ("Medium", "High"…). */
  label: string;
}

const RISK_LEVEL_TO_METER: Record<RiskLevel, RiskMeterDescriptor> = {
  [RiskLevel.LOW]: { scale: 3, level: 1, tone: 'success', label: 'Low' },
  [RiskLevel.MEDIUM]: { scale: 3, level: 2, tone: 'warning', label: 'Medium' },
  [RiskLevel.HIGH]: { scale: 3, level: 3, tone: 'error', label: 'High' },
  [RiskLevel.UNKNOWN]: { scale: 3, level: 0, tone: 'muted', label: 'Unknown' },
};

const AI_RISK_LEVEL_TO_METER: Record<AIRiskLevel, RiskMeterDescriptor> = {
  [AIRiskLevel.LOW]: { scale: 4, level: 1, tone: 'success', label: 'Low' },
  [AIRiskLevel.LIMITED]: { scale: 4, level: 2, tone: 'warning', label: 'Limited' },
  [AIRiskLevel.HIGH]: { scale: 4, level: 3, tone: 'error', label: 'High' },
  [AIRiskLevel.PROHIBITED]: { scale: 4, level: 4, tone: 'error-strong', label: 'Prohibited' },
  [AIRiskLevel.UNKNOWN]: { scale: 4, level: 0, tone: 'muted', label: 'Unknown' },
};

/** Resolve a `RiskLevel` (or null/undefined) to a meter descriptor.
 *  Unknown/missing values fall through to the UNKNOWN descriptor so
 *  the caller doesn't need to branch separately. */
export function riskLevelToMeter(score: RiskLevel | null | undefined): RiskMeterDescriptor {
  return score ? RISK_LEVEL_TO_METER[score] : RISK_LEVEL_TO_METER[RiskLevel.UNKNOWN];
}

/** Resolve an `AIRiskLevel` to a meter descriptor. */
export function aiRiskLevelToMeter(level: AIRiskLevel | null | undefined): RiskMeterDescriptor {
  return level ? AI_RISK_LEVEL_TO_METER[level] : AI_RISK_LEVEL_TO_METER[AIRiskLevel.UNKNOWN];
}

/**
 * DPIA aggregate risk (ICO 3-point overall — LOW / MEDIUM / HIGH per
 * Veritise DPIA spec §7). Same 3-bar scale + palette as the generic
 * `RISK_LEVEL_TO_METER` so overall-risk chips read visually identical
 * across surfaces, while the label + upstream data source is DPIA-specific.
 *
 * Missing / not-yet-assessed → `null` fallback below (level 0 · muted).
 */
const DPIA_OVERALL_TO_METER: Record<DpiaOverallRisk, RiskMeterDescriptor> = {
  [DpiaOverallRisk.LOW]: { scale: 3, level: 1, tone: 'success', label: 'Low' },
  [DpiaOverallRisk.MEDIUM]: { scale: 3, level: 2, tone: 'warning', label: 'Medium' },
  [DpiaOverallRisk.HIGH]: { scale: 3, level: 3, tone: 'error', label: 'High' },
};

/** Resolve a `DpiaOverallRisk` to a meter descriptor. */
export function dpiaOverallRiskToMeter(
  risk: DpiaOverallRisk | null | undefined
): RiskMeterDescriptor {
  return risk
    ? DPIA_OVERALL_TO_METER[risk]
    : { scale: 3, level: 0, tone: 'muted', label: 'Unknown' };
}
